/**
 * Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, type EntityState, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "..";
import { accessConfigService } from "~/services";
import type { AccessConfig, AccessConfigTemplate, DefaultRadioManager, RadioManager } from "~/types/accessConfig";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";
import { createSocketPatchReducer } from "../events/socketPatch";

export const accessConfigsAdapter = createEntityAdapter<AccessConfig>();
export const radioManagersAdapter = createEntityAdapter<RadioManager>();
export const accessConfigTemplatesAdapter = createEntityAdapter<AccessConfigTemplate>({
  selectId: (template) => template.type,
});
export const defaultRadioManagersAdapter = createEntityAdapter<DefaultRadioManager>();

interface AccessConfigState {
  loading: boolean;
  defaultRadioManagerLoading: boolean;
  configsData: EntityState<AccessConfig>;
  radioManagersData: EntityState<RadioManager>;
  templatesData: EntityState<AccessConfigTemplate>;
  defaultRadioManagersData: EntityState<DefaultRadioManager>;
  selectedConfigId: string | null;
  selectedDefaultRadioManagerId: string | null;
  open: boolean;
  defaultRadioManagerOpen: boolean;
  isEditing: boolean;
}

const initialState: AccessConfigState = {
  loading: true,
  defaultRadioManagerLoading: true,
  configsData: accessConfigsAdapter.getInitialState(),
  radioManagersData: radioManagersAdapter.getInitialState(),
  templatesData: accessConfigTemplatesAdapter.getInitialState(),
  defaultRadioManagersData: defaultRadioManagersAdapter.getInitialState(),
  selectedConfigId: null,
  selectedDefaultRadioManagerId: null,
  open: false,
  defaultRadioManagerOpen: false,
  isEditing: false,
};

export const fetchAccessConfigs = createAsyncThunk(
  "accessConfig/fetchAccessConfigs",
  async () => {
    try {
      const data = await accessConfigService.getAccessConfigData({ filter: { target: "DEVICE" } });
      return data as unknown as AccessConfig[];
    } catch (error) {
      console.error("Error fetching access config data:", error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; access config is admin-only and not surfaced in KPI UI
  { condition: () => !isPartnerKpiOnlyUser() },
);

export const fetchRadioManagerData = createAsyncThunk(
  "accessConfig/fetchRadioManagerData",
  async () => {
    try {
      const data = await accessConfigService.getAccessConfigData({ filter: { target: "RADIO" } });
      return data as unknown as RadioManager[];
    } catch (error) {
      console.error("Error fetching radio manager data:", error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; Radio Manager row in RadioSummary is operator-only
  { condition: () => !isPartnerKpiOnlyUser() },
);

export const fetchAccessConfigTemplates = createAsyncThunk(
  "accessConfig/fetchAccessConfigTemplates",
  async () => {
    try {
      const templates = await accessConfigService.getAccessConfigTemplate();
      return templates;
    } catch (error) {
      console.error("Error fetching access config templates:", error);
      throw error;
    }
  },
);

export const fetchDefaultRadioManager = createAsyncThunk(
  "accessConfig/fetchDefaultRadioManager",
  async () => {
    try {
      const data = await accessConfigService.getDefaultRadioManagerData();
      return data;
    } catch (error) {
      console.error("Error fetching default radio manager:", error);
      throw error;
    }
  },
);

const accessConfigSlice = createSlice({
  name: "accessConfig",
  initialState,
  reducers: {
    /** Store selection identity only; resolve the entity via selectors. */
    setSelectedConfig: (state, action: PayloadAction<string | null>) => {
      state.selectedConfigId = action.payload;
    },
    setDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDefaultRadioManagerDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.defaultRadioManagerOpen = action.payload;
    },
    setDefaultRadioManagerSelectedConfig: (state, action: PayloadAction<string | null>) => {
      state.selectedDefaultRadioManagerId = action.payload;
    },
    upsertDefaultRadioManager: (state, action: PayloadAction<DefaultRadioManager>) => {
      defaultRadioManagersAdapter.upsertOne(state.defaultRadioManagersData, action.payload);
      state.selectedDefaultRadioManagerId = action.payload.id;
    },
    applySocketPatchesAccessConfig: createSocketPatchReducer(accessConfigsAdapter, (s) => s.configsData),
    applySocketPatchesDefaultRadioManager: createSocketPatchReducer(defaultRadioManagersAdapter, (s) => s.defaultRadioManagersData),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccessConfigs.fulfilled, (state, action) => {
        state.loading = false;
        accessConfigsAdapter.setAll(state.configsData, action.payload);
      })
      // MCS-3868: clear loading on failure so the Access Config tab can't spin forever on a 403.
      .addCase(fetchAccessConfigs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchRadioManagerData.fulfilled, (state, action) => {
        radioManagersAdapter.setAll(state.radioManagersData, action.payload);
      })
      .addCase(fetchAccessConfigTemplates.fulfilled, (state, action) => {
        state.loading = false;
        accessConfigTemplatesAdapter.setAll(state.templatesData, action.payload);
      })
      .addCase(fetchDefaultRadioManager.fulfilled, (state, action) => {
        state.defaultRadioManagerLoading = false;
        defaultRadioManagersAdapter.setAll(state.defaultRadioManagersData, action.payload);
      });
  },
});

export const accessConfigActions = accessConfigSlice.actions;

const accessConfigEntitySelectors = accessConfigsAdapter.getSelectors(
  (state: RootState) => state.accessConfig.configsData,
);
const radioManagerEntitySelectors = radioManagersAdapter.getSelectors(
  (state: RootState) => state.accessConfig.radioManagersData,
);
const accessConfigTemplateEntitySelectors = accessConfigTemplatesAdapter.getSelectors(
  (state: RootState) => state.accessConfig.templatesData,
);
const defaultRadioManagerEntitySelectors = defaultRadioManagersAdapter.getSelectors(
  (state: RootState) => state.accessConfig.defaultRadioManagersData,
);

export const accessConfigData = accessConfigEntitySelectors.selectAll;
export const accessConfigEntities = accessConfigEntitySelectors.selectEntities;

export const radioManagerData = radioManagerEntitySelectors.selectAll;
export const radioManagerEntities = radioManagerEntitySelectors.selectEntities;

export const accessConfigTemplates = accessConfigTemplateEntitySelectors.selectAll;
export const accessConfigTemplateEntities = accessConfigTemplateEntitySelectors.selectEntities;

export const defaultRadioManagerData = defaultRadioManagerEntitySelectors.selectAll;
export const defaultRadioManagerEntities = defaultRadioManagerEntitySelectors.selectEntities;

export const accessConfigState = createSelector(
  [(state: RootState) => state.accessConfig.selectedConfigId, accessConfigEntities],
  (selectedId, entities): AccessConfig | null => selectedId ? entities[selectedId] ?? null : null,
);

export const defaultRadioManagerCurrent = createSelector(
  [ (state: RootState) => state.accessConfig.selectedDefaultRadioManagerId, defaultRadioManagerEntities, ],
  (selectedId, entities): DefaultRadioManager | null =>  selectedId ? entities[selectedId] ?? null : null,
);

export const accessConfigLoading = (state: RootState) => state.accessConfig.loading;
export const accessConfigOpen = (state: RootState) => state.accessConfig.open;
export const accessConfigIsEditing = (state: RootState) => state.accessConfig.isEditing;
export const defaultRadioManagerLoading = (state: RootState) => state.accessConfig.defaultRadioManagerLoading;
export const defaultRadioManagerOpen = (state: RootState) => state.accessConfig.defaultRadioManagerOpen;

export default accessConfigSlice.reducer;
